import React, {PropsWithChildren, useMemo, useState} from 'react';
import {ButtonDropdownField, DropdownList, FlexColumn, FlexRow, Icon, SubHeading} from '@forecast-it/design-system';
import {ItemType} from '@forecast-it/design-system/dist/forecast-v2/components/controls/dropdown/DropdownList';
import {FormattedMessage, useIntl} from 'react-intl';

type GroupingProps = {
	value: string;
};

type ChildType = React.ReactElement<GroupingProps & PropsWithChildren<ItemType>>;
export type OnGroupingsChange = (groupings: string[]) => void;
export type GroupingsProps = {
	groupings: string[];
	onChange: OnGroupingsChange;
	maxLevel: 1 | 2 | 3;
	children: ChildType[];
};

const ChangeViewGroupings = ({maxLevel, groupings: initialGroupings, onChange, children}: GroupingsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const [groupings, setGroupings] = useState<Set<string>>(() => new Set(initialGroupings));
	const [showDropdown, setShowDropdown] = useState(() => groupings.size === 0);

	const addGrouping = (newGrouping: string) => {
		setGroupings(prevGroupings => {
			const newGroupings = prevGroupings.add(newGrouping);
			onChange([...newGroupings]);
			return new Set(newGroupings);
		});
		setShowDropdown(false);
	};
	const removeGrouping = (e: Event, toBeRemovedGrouping: string) => {
		e.preventDefault();
		setGroupings(prevGroupings => {
			const newGroupings = [...prevGroupings].filter(prevGrouping => prevGrouping !== toBeRemovedGrouping);
			onChange([...newGroupings]);
			return new Set(newGroupings);
		});
		if (groupings.size <= 1) {
			setShowDropdown(true);
		}
	};

	const childrenArray = useMemo(() => React.Children.toArray(children), [children]);
	const selectedGroupings = useMemo(() => {
		const order = [...groupings];
		return childrenArray
			.filter((child: ChildType) => groupings.has(child.props.value))
			.sort((a: ChildType, b: ChildType) => order.indexOf(a.props.value) - order.indexOf(b.props.value));
	}, [childrenArray, groupings]);

	const groupingOptions = childrenArray.filter((child: ChildType) => !groupings.has(child.props.value));
	const showAddAnotherGroupLevel = groupings.size < maxLevel && !showDropdown && groupingOptions.length > 0;

	return (
		<DropdownList.ContentGroup title={formatMessage({id: 'change_view.groupings'})}>
			{selectedGroupings.map((child: ChildType, index) => (
				<DropdownList.Item
					key={child.props.value}
					onClick={e => removeGrouping(e, child.props.value)}
					data-cy={'selected-grouping-' + child.props.value}
				>
					<FlexRow justifyContent={'space-between'}>
						<FlexColumn>
							{selectedGroupings.length > 1 ? (
								<SubHeading>
									<FormattedMessage id={'expense_management.grouping_level'} values={{count: index + 1}} />
								</SubHeading>
							) : undefined}
							{child.props.children}
						</FlexColumn>
						<Icon icon={'close'} />
					</FlexRow>
				</DropdownList.Item>
			))}
			{showAddAnotherGroupLevel ? (
				<DropdownList.ButtonItem onClick={() => setShowDropdown(true)} data-cy={'add-another-grouping'}>
					<Icon icon={'plus'} /> <FormattedMessage id={'expense_management.grouping.add_another_group'} />
				</DropdownList.ButtonItem>
			) : undefined}
			{showDropdown ? (
				<DropdownList.DropdownItem>
					<DropdownList.Trigger>
						<ButtonDropdownField isDefaultLabel={true} data-cy={'select-grouping'}>
							<FormattedMessage id={'expense_management.grouping.select_group'} />
						</ButtonDropdownField>
					</DropdownList.Trigger>
					<DropdownList.Content offset={4} useTriggerWidth>
						{groupingOptions.map((child: ChildType) => (
							<DropdownList.Item
								key={child.props.value}
								onClick={() => addGrouping(child.props.value)}
								data-cy={'grouping-option-' + child.props.value}
							>
								{child.props.children}
							</DropdownList.Item>
						))}
					</DropdownList.Content>
				</DropdownList.DropdownItem>
			) : undefined}
		</DropdownList.ContentGroup>
	);
};

const Grouping: React.FC<PropsWithChildren<GroupingProps>> = () => null;

ChangeViewGroupings.Grouping = Grouping;

export default ChangeViewGroupings;
